import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaCheckCircle } from 'react-icons/fa';

const QuickQuoteCalculator = () => {
  const [selectedService, setSelectedService] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [urgency, setUrgency] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const services = [
    { id: 'repair', label: 'Reparo', icon: '🔧' },
    { id: 'maintenance', label: 'Manutenção Preventiva', icon: '🛠️' },
    { id: 'upgrade', label: 'Upgrade', icon: '⚡' },
    { id: 'format', label: 'Formatação', icon: '💾' },
    { id: 'diagnosis', label: 'Diagnóstico', icon: '🔍' }
  ];

  const devices = [
    { id: 'notebook', label: 'Notebook', icon: '💻' },
    { id: 'pc', label: 'PC Desktop', icon: '🖥️' },
    { id: 'tv', label: 'TV', icon: '📺' },
    { id: 'microwave', label: 'Microondas', icon: '🔥' },
    { id: 'nobreak', label: 'No-break', icon: '🔋' }
  ];

  // Multiplicadores de urgência - Ajuste os valores se necessário
  // O preço base será multiplicado por estes valores conforme a urgência
  const urgencyOptions = [
    { id: 'normal', label: 'Normal (5-10 dias)', multiplier: 1 },      // Sem acréscimo
    { id: 'urgent', label: 'Urgente (2-5 dias)', multiplier: 1.3 },     // +30% sobre o valor base
    { id: 'emergency', label: 'Emergência (1-2 dias)', multiplier: 1.5 } // +50% sobre o valor base
  ];

  // ============================================
  // TABELA DE PREÇOS BASE - ALTERE OS VALORES AQUI
  // ============================================
  // Valores em R$ (Reais) - Preços base para cada tipo de serviço e equipamento
  // IMPORTANTE: Altere os valores abaixo conforme os preços reais da sua loja
  // ============================================
  const basePrices = {
    // REPARO - Valores base para reparos
    repair: {
      notebook: 200,    // Reparo de Notebook
      pc: 150,          // Reparo de PC Desktop
      tv: 200,          // Reparo de TV
      microwave: 175,   // Reparo de Microondas
      nobreak: 175      // Reparo de No-break
    },
    // MANUTENÇÃO PREVENTIVA - Valores base para manutenção
    maintenance: {
      notebook: 80,      // Manutenção de Notebook
      pc: 70,           // Manutenção de PC Desktop
      tv: 100,          // Manutenção de TV
      microwave: 60,    // Manutenção de Microondas
      nobreak: 80       // Manutenção de No-break
    },
    // UPGRADE - Valores base para upgrades
    upgrade: {
      notebook: 200,    // Upgrade de Notebook
      pc: 180,          // Upgrade de PC Desktop
      tv: 0,            // Não aplicável para TV
      microwave: 0,    // Não aplicável para Microondas
      nobreak: 0        // Não aplicável para No-break
    },
    // FORMATAÇÃO - Valores base para formatação
    format: {
      notebook: 100,    // Formatação de Notebook
      pc: 80,           // Formatação de PC Desktop
      tv: 0,            // Não aplicável para TV
      microwave: 0,     // Não aplicável para Microondas
      nobreak: 0        // Não aplicável para No-break
    },
    // DIAGNÓSTICO - Valores base para diagnóstico
    diagnosis: {
      notebook: 50,     // Diagnóstico de Notebook
      pc: 50,           // Diagnóstico de PC Desktop
      tv: 60,           // Diagnóstico de TV
      microwave: 50,    // Diagnóstico de Microondas
      nobreak: 50       // Diagnóstico de No-break
    }
  };
  // ============================================
  // FIM DA TABELA DE PREÇOS
  // ============================================

  const calculateEstimate = () => {
    if (!selectedService || !selectedDevice || !urgency) {
      return;
    }

    const basePrice = basePrices[selectedService]?.[selectedDevice] || 0;
    
    if (basePrice === 0) {
      setEstimatedPrice(null);
      setShowResult(true);
      return;
    }

    const urgencyMultiplier = urgencyOptions.find(u => u.id === urgency)?.multiplier || 1;
    const finalPrice = Math.round(basePrice * urgencyMultiplier);

    setEstimatedPrice(finalPrice);
    setShowResult(true);
  };

  const resetCalculator = () => {
    setSelectedService('');
    setSelectedDevice('');
    setUrgency('');
    setEstimatedPrice(null);
    setShowResult(false);
  };

  const canCalculate = selectedService && selectedDevice && urgency;

  return (
    <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
      <div className="container-responsive">
        {/* Cabeçalho */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-8 xs:mb-10 sm:mb-12 md:mb-16"
        >
          <div className="flex items-center justify-center gap-3 xs:gap-4 mb-4">
            <FaCalculator className="w-8 h-8 xs:w-10 xs:h-10 sm:w-12 sm:h-12 
                                    text-primary-600" />
            <h2 className="text-2xl xs:text-3xl xs2:text-3xl xs3:text-4xl sm:text-4xl 
                         md:text-5xl lg:text-6xl font-bold text-gray-800">
              <span className="text-gradient">Calculadora de Orçamento Rápido</span>
            </h2>
          </div>
          <p className="text-sm xs:text-base sm:text-lg md:text-xl text-gray-600 
                       max-w-2xl mx-auto px-4">
            Obtenha uma estimativa rápida do valor do seu serviço
          </p>
          <div className="w-16 xs:w-20 sm:w-24 md:w-32 h-0.5 xs:h-1 bg-primary-600 
                        mx-auto mt-4 xs:mt-5 sm:mt-6 rounded-full"></div>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="card-modern"
          >
            {!showResult ? (
              <div className="space-y-6 xs:space-y-8">
                {/* Tipo de Serviço */}
                <div>
                  <label className="block text-sm xs:text-base sm:text-lg font-semibold 
                                  text-gray-800 mb-3 xs:mb-4">
                    Tipo de Serviço
                  </label>
                  <div className="grid grid-cols-2 xs4:grid-cols-3 sm:grid-cols-5 gap-3 xs:gap-4">
                    {services.map((service) => (
                      <motion.button
                        key={service.id}
                        onClick={() => setSelectedService(service.id)}
                        className={`p-3 xs:p-4 rounded-xl border-2 transition-all duration-300
                                  text-sm xs:text-base font-medium
                                  ${
                                    selectedService === service.id
                                      ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-gray-50'
                                  }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="text-2xl xs:text-3xl mb-2">{service.icon}</div>
                        <div>{service.label}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Tipo de Equipamento */}
                <div>
                  <label className="block text-sm xs:text-base sm:text-lg font-semibold 
                                  text-gray-800 mb-3 xs:mb-4">
                    Tipo de Equipamento
                  </label>
                  <div className="grid grid-cols-2 xs4:grid-cols-3 sm:grid-cols-5 gap-3 xs:gap-4">
                    {devices.map((device) => (
                      <motion.button
                        key={device.id}
                        onClick={() => setSelectedDevice(device.id)}
                        className={`p-3 xs:p-4 rounded-xl border-2 transition-all duration-300
                                  text-sm xs:text-base font-medium
                                  ${
                                    selectedDevice === device.id
                                      ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-gray-50'
                                  }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="text-2xl xs:text-3xl mb-2">{device.icon}</div>
                        <div>{device.label}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Urgência */}
                <div>
                  <label className="block text-sm xs:text-base sm:text-lg font-semibold 
                                  text-gray-800 mb-3 xs:mb-4">
                    Urgência do Serviço
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 xs:gap-4">
                    {urgencyOptions.map((option) => (
                      <motion.button
                        key={option.id}
                        onClick={() => setUrgency(option.id)}
                        className={`p-4 xs:p-5 rounded-xl border-2 transition-all duration-300
                                  text-sm xs:text-base font-medium text-left
                                  ${
                                    urgency === option.id
                                      ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-gray-50'
                                  }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${
                                          urgency === option.id
                                            ? 'border-primary-600 bg-primary-600'
                                            : 'border-gray-300'
                                        }`}>
                            {urgency === option.id && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-2 h-2 bg-white rounded-full"
                              />
                            )}
                          </div>
                          <span>{option.label}</span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Botão Calcular */}
                <motion.button
                  onClick={calculateEstimate}
                  disabled={!canCalculate}
                  className={`w-full py-4 xs:py-5 rounded-xl font-bold text-base xs:text-lg
                            transition-all duration-300
                            ${
                              canCalculate
                                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                  whileHover={canCalculate ? { scale: 1.02 } : {}}
                  whileTap={canCalculate ? { scale: 0.98 } : {}}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  Calcular Estimativa
                </motion.button>
              </div>
            ) : (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-6 xs:py-8"
                >
                  {estimatedPrice !== null ? (
                    <>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                        className="w-20 h-20 xs:w-24 xs:h-24 mx-auto mb-6 rounded-full
                                  bg-gradient-to-br from-green-500 to-green-600
                                  flex items-center justify-center shadow-lg"
                      >
                        <FaCheckCircle className="w-10 h-10 xs:w-12 xs:h-12 text-white" />
                      </motion.div>
                      <h3 className="text-xl xs:text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                        Estimativa de Orçamento
                      </h3>
                      <div className="mb-6">
                        <p className="text-sm xs:text-base text-gray-600 mb-2">
                          Valor estimado:
                        </p>
                        <p className="text-4xl xs:text-5xl sm:text-6xl font-bold text-primary-600">
                          R$ {estimatedPrice.toLocaleString('pt-BR')}
                        </p>
                        <p className="text-xs xs:text-sm text-gray-500 mt-2">
                          * Valor aproximado. Orçamento final pode variar após avaliação técnica.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                        className="w-20 h-20 xs:w-24 xs:h-24 mx-auto mb-6 rounded-full
                                  bg-gradient-to-br from-blue-500 to-blue-600
                                  flex items-center justify-center shadow-lg"
                      >
                        <span className="text-4xl xs:text-5xl">💬</span>
                      </motion.div>
                      <h3 className="text-xl xs:text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                        Precisa de Avaliação Personalizada
                      </h3>
                      <p className="text-base xs:text-lg text-gray-600 mb-6">
                        Para este tipo de serviço, recomendamos uma avaliação técnica 
                        presencial para um orçamento preciso.
                      </p>
                    </>
                  )}

                  <div className="flex flex-col xs:flex-row gap-3 xs:gap-4 justify-center">
                    <motion.button
                      onClick={resetCalculator}
                      className="btn-secondary text-sm xs:text-base px-6 xs:px-8 py-3 xs:py-3.5"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Nova Estimativa
                    </motion.button>
                    <motion.a
                      href="#formularios-combinados"
                      className="btn-primary text-sm xs:text-base px-6 xs:px-8 py-3 xs:py-3.5"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Solicitar Orçamento Completo
                    </motion.a>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </motion.div>

          {/* Aviso */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-6 xs:mt-8 text-xs xs:text-sm text-gray-500 text-center max-w-2xl mx-auto"
          >
            ⚠️ Esta é apenas uma estimativa rápida. O valor final pode variar após 
            avaliação técnica detalhada do equipamento. Entre em contato para um 
            orçamento preciso e personalizado.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default QuickQuoteCalculator;

